import { User, MessageCircle, Shield, Radar, Heart, X, ArrowRight, Bell, Check, Handshake, Sparkles, Coffee } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import koffieCoAvatar from "@/assets/koffie-co-avatar.jpg";

const steps = [
  {
    icon: User,
    step: "01",
    title: "Maak profiel",
    text: "In 1 minuut klaar — vertel wie je bent en wat je zoekt.",
  },
  {
    icon: Radar,
    step: "02",
    title: "Zet radar aan",
    text: "Jij bepaalt je zichtbaarheid en je status.",
  },
  {
    icon: MessageCircle,
    step: "03",
    title: "Ontvang berichten",
    text: "Werkgevers benaderen jou. Jij kiest met wie je praat.",
  },
];

const afterSteps = [
  {
    icon: Coffee,
    title: "Start een gesprek",
    text: "Chat rechtstreeks met werkgevers en kijk of er een match is.",
  },
  {
    icon: Sparkles,
    title: "Luna kijkt met je mee",
    text: "Jouw AI-coach houdt nieuwe vacatures in de gaten en tipt je als er iets bij je past.",
  },
  {
    icon: Handshake,
    title: "Liever zelf solliciteren?",
    text: "Check de nieuwste vacatures in je dashboard. Vraag? Chat gewoon met de werkgever!",
  },
];

export const RadarStepsSection = () => {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLElement | null>(null);
  const [doneCount, setDoneCount] = useState(0);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          observer.disconnect();
          setDoneCount(1);
          timers.push(setTimeout(() => setDoneCount(2), 400));
          timers.push(setTimeout(() => setDoneCount(3), 800));
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      timers.forEach(clearTimeout);
    };
  }, []);

  const allDone = doneCount >= 3;

  return (
    <section ref={sectionRef} className="relative bg-white py-12 md:py-16 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-25">
        <div className="absolute -top-10 left-0 w-72 h-72 bg-cyan/20 rounded-full blur-[110px]" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow/20 rounded-full blur-[130px]" />
      </div>

      <div className="container mx-auto px-4 md:px-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-left mb-8 md:mb-10">
            <h2 className="font-heading font-black text-3xl md:text-5xl leading-[1.05] tracking-tight">
              In 3 stappen{" "}
              <span className="relative inline-block">
                <span className="relative z-10">op de radar</span>
                <span className="absolute left-0 -bottom-1 w-full h-2.5 md:h-3 bg-yellow -z-0 rounded-sm" />
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-10 lg:gap-24 items-center">
          {/* Left: steps stacked */}
          <div className="relative">
            {/* connection line */}
            <div className="absolute left-9 md:left-10 top-10 bottom-10 w-0 border-l border-dashed border-cyan-dark/25" />

            <div className="space-y-3">
              {steps.map(({ step, title, text }, i) => (
                <div
                  key={step}
                  className="group relative flex items-center gap-4 rounded-2xl border border-border/50 bg-white p-4 md:p-5 shadow-[0_10px_30px_-20px_hsl(var(--cyan-dark)/0.45)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_22px_50px_-26px_hsl(var(--cyan-dark)/0.55)]"
                >
                  <div className="relative shrink-0">
                    <div
                      className={`w-10 h-10 md:w-11 md:h-11 rounded-full font-heading font-black text-base md:text-lg flex items-center justify-center transition-all duration-500 ${
                        doneCount > i
                          ? "bg-yellow text-yellow-foreground scale-105 radar-step-done"
                          : "bg-cyan-light text-cyan-dark"
                      }`}
                    >
                      {doneCount > i ? <Check className="w-5 h-5 animate-scale-in" strokeWidth={3} /> : i + 1}
                    </div>
                    {i === steps.length - 1 && allDone && (
                      <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-yellow border-2 border-white animate-scale-in" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading font-bold text-base md:text-lg">{title}</h3>
                      {i === steps.length - 1 && allDone && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-cyan-light px-2 py-0.5 text-[11px] font-bold text-cyan-dark animate-scale-in">
                          <Bell className="w-3 h-3" strokeWidth={2.6} />
                          Nieuw bericht
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-foreground/65 leading-relaxed mt-0.5">{text}</p>
                  </div>

                  {/* dashed arrow from step 3 to the mockup */}
                  {i === steps.length - 1 && (
                    <svg
                      className={`hidden lg:block absolute left-full top-1/2 -translate-y-1/2 ml-2 w-24 h-10 pointer-events-none overflow-visible transition-opacity duration-500 ${
                        allDone ? "opacity-100" : "opacity-0"
                      }`}
                      viewBox="0 0 96 40"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M2 30 C 30 30, 50 14, 86 12"
                        stroke="hsl(var(--cyan-dark) / 0.4)"
                        strokeWidth="2"
                        strokeLinecap="round"
                        className="radar-dash-flow"
                      />
                      <path
                        d="M78 6 L88 12 L78 18"
                        stroke="hsl(var(--cyan-dark) / 0.5)"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                    </svg>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Right: 3D tilted mockup */}
          <div>
            <div
              className={`relative overflow-hidden rounded-3xl p-6 sm:p-8 border-2 border-yellow/40 bg-gradient-to-br from-white via-yellow/10 to-cyan-light/40 shadow-[0_50px_90px_-40px_hsl(var(--cyan-dark)/0.6),0_20px_40px_-20px_rgba(0,0,0,0.25)] rotate-[2.5deg] transition-all duration-500 ease-out hover:-translate-y-1 hover:rotate-[1deg] ${
                allDone ? "opacity-100 scale-100" : "opacity-0 scale-90"
              }`}
            >
              <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-yellow/30 blur-3xl pointer-events-none" />
              <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-cyan/20 blur-3xl pointer-events-none" />

              <div className="relative flex items-center gap-3 mb-4">
                <div className="relative">
                  <span className="absolute inset-0 rounded-full bg-green-400/50 radar-ping" />
                  <span className="relative w-3 h-3 rounded-full bg-green-500 inline-block" />
                </div>
                <span className="text-sm font-bold">Koffie &amp; Co heeft bij jou gesolliciteerd</span>
              </div>

              <div className="relative p-4 sm:p-5 rounded-xl bg-white/85 backdrop-blur-sm border border-foreground/5 space-y-3">
                <div className="flex items-center gap-3">
                  <img
                    src={koffieCoAvatar}
                    alt="Koffie &amp; Co"
                    loading="lazy"
                    width={816}
                    height={816}
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-cover shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm sm:text-base truncate">Koffie &amp; Co</p>
                    <p className="text-xs sm:text-sm text-foreground/60 truncate">
                      wil graag kennismaken ☕
                    </p>
                  </div>
                </div>
                <div className="border-t border-foreground/10" />
                <p className="text-sm sm:text-base text-foreground/90 leading-relaxed">
                  "Hey! We zoeken een nieuwe klantenservice medewerker en jouw profiel past perfect bij ons team. Zin om binnenkort een bakkie te doen?"
                </p>
                <div className="flex items-center gap-2 pt-1">
                  <div className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-md bg-yellow text-yellow-foreground font-bold text-sm py-2.5">
                    <Heart className="w-4 h-4" />
                    Accepteer
                  </div>
                  <div className="inline-flex items-center justify-center rounded-md border border-border bg-white text-foreground/70 px-3 py-2.5">
                    <X className="w-4 h-4" />
                  </div>
                </div>
                <p className="text-[11px] text-foreground/50 italic text-center pt-1">
                  Jouw gegevens worden pas zichtbaar wanneer je accepteert 🔒
                </p>
              </div>
            </div>
          </div>
          </div>

          {/* En daarna? */}
          <div className="mt-10 md:mt-14">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px flex-1 bg-border/60" />
              <span className="font-heading font-bold text-sm md:text-base text-foreground/70">
                En daarna?
              </span>
              <span className="h-px flex-1 bg-border/60" />
            </div>

            <div className="rounded-2xl bg-cyan-light/50 px-5 py-5 md:px-7 md:py-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-0">
                {afterSteps.map(({ title, text }, i) => (
                  <div
                    key={title}
                    className={`sm:px-5 ${i === 0 ? "sm:pl-0" : "sm:border-l sm:border-cyan-dark/15"} ${
                      i === afterSteps.length - 1 ? "sm:pr-0" : ""
                    }`}
                  >
                    <h3 className="font-heading font-bold text-sm md:text-base">{title}</h3>
                    <p className="text-sm text-foreground/65 leading-snug mt-0.5">{text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Privacy onderaan */}
          <div className="mt-8 md:mt-12 rounded-2xl border border-border/50 bg-white px-5 py-4 md:px-6 md:py-5 shadow-[0_16px_40px_-30px_hsl(var(--cyan-dark)/0.5)]">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-9 h-9 rounded-full bg-foreground/80 flex items-center justify-center shrink-0">
                  <Shield className="w-4 h-4 text-background" strokeWidth={2.5} />
                </div>
                <p className="text-sm text-foreground/70">
                  <span className="font-heading font-bold text-foreground">Jouw privacy staat voorop</span>{" "}
                  — bedrijven zien alleen je voornaam, jij bepaalt wat je deelt.
                </p>
              </div>
              <Button
                className="bg-yellow text-yellow-foreground hover:opacity-90 font-bold rounded-full px-6 shrink-0"
                onClick={() => navigate("/signup/kandidaat?src=stappen")}
              >
                Zet mij op de radar
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
